import {
  Alert,
  Badge,
  Button,
  Card,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
  type Tone,
} from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { formatDate, formatMoney } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

type LimitKey = 'whatsappMessagesPerMonth' | 'stores' | 'members';

interface Details {
  legalName: string | null;
  gstin: string | null;
  address: string | null;
  stateCode: string | null;
}

interface Billing {
  current: {
    state: 'trialing' | 'active' | 'free';
    planCode: string | null;
    planName: string;
    periodStart: string;
    periodEnd: string | null;
    autoRenew: boolean;
    provider: string | null;
  };
  usage: { key: LimitKey; used: number; limit: number | null }[];
  plans: {
    code: string;
    name: string;
    currency: string;
    priceMinor: number;
    limits: Partial<Record<LimitKey, number | null>>;
    current: boolean;
  }[];
  payments: {
    id: string;
    plan: string;
    amountMinor: number;
    currency: string;
    status: 'created' | 'paid' | 'expired';
    paidAt: string | null;
    createdAt: string;
    payUrl: string | null;
    receipt: boolean;
    invoiceNumber: string | null;
  }[];
  paymentsConfigured: boolean;
  billedByShopify: boolean;
  gst: { enabled: boolean; ratePercent?: number };
  details: Details;
  states: { code: string; name: string }[];
}

const LIMIT_LABEL: Record<LimitKey, string> = {
  whatsappMessagesPerMonth: 'WhatsApp template messages',
  stores: 'Stores',
  members: 'Team members',
};
const STATE_LABEL = { trialing: 'Trial', active: 'Active', free: 'Free plan' } as const;
const PAYMENT_TONE: Record<Billing['payments'][number]['status'], Tone> = {
  created: 'warning',
  paid: 'success',
  expired: 'neutral',
};
const PAYMENT_LABEL = { created: 'Awaiting payment', paid: 'Paid', expired: 'Expired' } as const;

const money = (minor: number, currency: string) =>
  formatMoney(String(minor / 100), currency, { whole: true });
const limitText = (v: number | null | undefined) =>
  v === null || v === undefined ? 'Unlimited' : v.toLocaleString();

/** Name, GSTIN and state that print on tax invoices. Needed before buying when GST is on. */
function InvoiceDetails({ billing, onSaved }: { billing: Billing; onSaved: () => void }) {
  const toast = useToast();
  const submit = useSubmit();
  const [form, setForm] = useState({
    legalName: billing.details.legalName ?? '',
    gstin: billing.details.gstin ?? '',
    address: billing.details.address ?? '',
    stateCode: billing.details.stateCode ?? '',
  });
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.put('/billing/details', form);
      return true;
    });
    if (ok) {
      toast.success('Invoice details saved');
      onSaved();
    }
  };
  return (
    <Card
      title="Invoice details"
      subtitle="Printed on your GST tax invoices. The state decides whether CGST and SGST or IGST applies."
    >
      <form className="stack" onSubmit={(e) => void onSubmit(e)}>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <div className="rule-grid">
          <TextField
            label="Business name"
            value={form.legalName}
            error={submit.fieldErrors['legalName']}
            onChange={(e) => set('legalName')(e.target.value)}
          />
          <TextField
            label="GSTIN (optional)"
            value={form.gstin}
            error={submit.fieldErrors['gstin']}
            maxLength={15}
            onChange={(e) => set('gstin')(e.target.value.toUpperCase())}
          />
          <SelectField
            label="State"
            value={form.stateCode}
            error={submit.fieldErrors['stateCode']}
            onChange={(e) => set('stateCode')(e.target.value)}
            options={[
              { value: '', label: form.gstin ? 'From the GSTIN' : 'Choose a state' },
              ...billing.states.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` })),
            ]}
          />
          <TextField
            label="Address (optional)"
            value={form.address}
            onChange={(e) => set('address')(e.target.value)}
          />
        </div>
        <div>
          <Button type="submit" loading={submit.pending}>
            Save details
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function BillingPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const billing = useLoader(() => api.get<Billing>('/billing'), tenantId);
  const [buying, setBuying] = useState<string | null>(null);

  if (billing.loading && !billing.data) return <Spinner size="lg" label="Loading" />;
  if (billing.error || !billing.data) {
    return <Alert tone="critical">{billing.error ?? 'Failed to load'}</Alert>;
  }
  const b = billing.data;
  const canManage = can('billing:manage');
  const gstOn = b.gst.enabled && !b.billedByShopify;
  const detailsReady = !gstOn || (!!b.details.legalName && !!b.details.stateCode);
  const canBuy = (b.paymentsConfigured || b.billedByShopify) && detailsReady;

  const buy = async (planCode: string, autoRenew: boolean) => {
    setBuying(`${planCode}:${autoRenew}`);
    try {
      const { url } = await api.post<{ url: string }>('/billing/checkout', { planCode, autoRenew });
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err));
      setBuying(null);
    }
  };

  const cancelAutoRenew = async () => {
    const ok = await confirm({
      title: 'Cancel automatic renewal',
      message: `Your ${b.current.planName} plan stays active until ${
        b.current.periodEnd ? formatDate(b.current.periodEnd) : 'the end of the period'
      }, then moves to the Free plan unless you renew.`,
      confirmLabel: 'Cancel renewal',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post('/billing/auto-renew/cancel', {});
      void billing.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title="Billing"
        description="Your plan, what you have used, and your payments. Meta's own WhatsApp conversation charges are billed separately by Meta."
      />
      {b.current.state === 'trialing' && b.current.periodEnd && (
        <Alert tone="info" title="You are on a free trial">
          Your {b.current.planName} trial ends on {formatDate(b.current.periodEnd)}. After that the
          Free plan applies unless you choose a plan.
        </Alert>
      )}
      {b.current.state === 'free' && (
        <Alert tone="warning" title="You are on the Free plan">
          Limits are small. Choose a plan below to send more.
        </Alert>
      )}
      {!b.paymentsConfigured && !b.billedByShopify && canManage && (
        <Alert tone="info" title="Online payment is pending configuration">
          Razorpay keys have not been added to this installation yet, so plans cannot be bought
          here.
        </Alert>
      )}
      {b.billedByShopify && (
        <Alert tone="info" title="Billed through Shopify">
          Your plan is charged on your Shopify invoice. You approve each plan change in Shopify.
        </Alert>
      )}
      {gstOn && (
        <Alert tone="info" title={`Prices exclude GST (${b.gst.ratePercent}%)`}>
          GST is added at checkout and shown on your tax invoice.
        </Alert>
      )}

      <Card
        title={`Current plan: ${b.current.planName}`}
        subtitle={`${STATE_LABEL[b.current.state]}${
          b.current.periodEnd ? ` · until ${formatDate(b.current.periodEnd)}` : ''
        }`}
        {...(b.current.autoRenew && canManage
          ? {
              actions: (
                <Button size="sm" variant="secondary" onClick={() => void cancelAutoRenew()}>
                  Cancel auto-renewal
                </Button>
              ),
            }
          : {})}
      >
        {b.current.autoRenew && (
          <p className="text-muted">
            <Badge tone="success">Auto-renews</Badge> This plan renews automatically every month.
          </p>
        )}
        <div className="rule-grid">
          {b.usage.map((u) => (
            <div key={u.key} className="stack">
              <div className="row">
                <span>{LIMIT_LABEL[u.key]}</span>
                <span className="text-muted">
                  {u.used.toLocaleString()} / {limitText(u.limit)}
                </span>
              </div>
              {u.limit !== null && (
                <meter
                  className="usage-meter"
                  min={0}
                  max={Math.max(u.limit, 1)}
                  low={Math.max(u.limit, 1) * 0.8}
                  high={Math.max(u.limit, 1) * 0.95}
                  optimum={0}
                  value={Math.min(u.used, Math.max(u.limit, 1))}
                  aria-label={`${LIMIT_LABEL[u.key]} used`}
                />
              )}
            </div>
          ))}
        </div>
        <p className="text-muted text-sm">
          WhatsApp messages count business-initiated templates this period. Replies inside a
          customer&rsquo;s 24-hour window are not counted.
        </p>
      </Card>

      {gstOn && canManage && <InvoiceDetails billing={b} onSaved={() => void billing.reload()} />}
      {gstOn && !detailsReady && canManage && (
        <Alert tone="warning">Save your invoice details above to enable checkout.</Alert>
      )}

      <div className="rule-grid">
        {b.plans.map((p) => (
          <div
            key={p.code}
            className={`rule-card stack plan-card${p.current ? ' plan-card--current' : ''}`}
          >
            <div className="rule-card__head">
              <div className="rule-card__title">{p.name}</div>
              {p.current && <Badge tone="success">Current</Badge>}
            </div>
            <div className="plan-card__price">
              {p.priceMinor === 0 ? 'Free' : money(p.priceMinor, p.currency)}
              {p.priceMinor > 0 && <span className="text-muted text-sm"> / 30 days</span>}
            </div>
            <ul className="plan-card__limits">
              <li>{limitText(p.limits.whatsappMessagesPerMonth)} template messages</li>
              <li>
                {limitText(p.limits.stores)} {p.limits.stores === 1 ? 'store' : 'stores'}
              </li>
              <li>{limitText(p.limits.members)} team members</li>
            </ul>
            {p.priceMinor > 0 && canManage && b.billedByShopify && (
              <Button
                variant={p.current ? 'secondary' : 'primary'}
                disabled={!canBuy || buying !== null}
                loading={buying === `${p.code}:false`}
                onClick={() => void buy(p.code, false)}
              >
                {p.current ? 'Renew in Shopify' : 'Choose plan'}
              </Button>
            )}
            {p.priceMinor > 0 && canManage && !b.billedByShopify && (
              <div className="stack">
                <Button
                  variant={p.current ? 'secondary' : 'primary'}
                  disabled={!canBuy || buying !== null}
                  loading={buying === `${p.code}:true`}
                  onClick={() => void buy(p.code, true)}
                >
                  Subscribe, renews monthly
                </Button>
                <Button
                  variant="secondary"
                  disabled={!canBuy || buying !== null}
                  loading={buying === `${p.code}:false`}
                  onClick={() => void buy(p.code, false)}
                >
                  {p.current ? 'Renew once for 30 days' : 'Pay once for 30 days'}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Card
        title="Payments"
        subtitle="One-time payments buy 30 days. Subscriptions are charged every month until cancelled."
        flush
      >
        {b.payments.length === 0 ? (
          <div className="card__body">
            <p className="text-muted">No payments yet.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Invoice</th>
                  <th>Status</th>
                  <th className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {b.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{formatDate(p.paidAt ?? p.createdAt)}</td>
                    <td>{p.plan}</td>
                    <td>{money(p.amountMinor, p.currency)}</td>
                    <td>{p.invoiceNumber ?? '—'}</td>
                    <td>
                      <Badge tone={PAYMENT_TONE[p.status]}>{PAYMENT_LABEL[p.status]}</Badge>
                    </td>
                    <td className="table__actions">
                      {p.receipt && (
                        <Link
                          className="btn btn--secondary btn--sm"
                          to={`/billing/receipt/${p.id}`}
                        >
                          {p.invoiceNumber ? 'Invoice' : 'Receipt'}
                        </Link>
                      )}
                      {p.payUrl && (
                        <a className="btn btn--secondary btn--sm" href={p.payUrl}>
                          Pay now
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
